﻿import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Popover } from "@blueprintjs/core";
import moment from "moment";
import 'components/form-fields/FormFields.css';
import { DatePicker } from '@blueprintjs/datetime';
import { enGB } from "date-fns/locale";
import { ShowHide } from 'components/layout';
import { Button } from 'components/buttons';
import classNames from "classnames";
import getIsoPeriodStart, { isoWeeksPerPeriod } from "utils/getIsoPeriodStart";

export function FormDatePeriodSelector(
    {
        id = null,
        startDate = null,
        minPeriods = null,
        maxPeriods = null,
        periodType = "months",
        periodsToShow = 1,
        initialPeriodOffset = 0,
        dateFormat = "DD/MM/YYYY",
        showCalendar = false,
        calendarMinDate = null,
        calendarMaxDate = null,
        calendarModifiers = null,
        showText = true,
        usePortal = true,
        showResetButton = true,
        resetDate = null,
        loading = false,
        recalcPeriodWithDate = null,
        reverse = false,
        firstDayOfWeek = 1,
        disabled,
        maxDate,
        minDate,
        onChange
    }
) {
    const dateLocale = "en-gb";
    const monthsInPastDefault = 30;
    const yearsInFutureDefault = 5;
    const minDateDefault = moment().subtract(monthsInPastDefault, 'months').toDate();
    const maxDateDefault = moment().add(yearsInFutureDefault, 'years').toDate();
    const [periodStart, setPeriodStart] = useState(null);
    const [periodEnd, setPeriodEnd] = useState(null);
    const [periodOffset, setPeriodOffset] = useState(null);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [baseDate, setBaseDate] = useState(startDate);
    const [initialOffset, setInitialOffset] = useState(null);
    const [minDateValue, setMinDate] = useState(minDateDefault);
    const [maxDateValue, setMaxDate] = useState(maxDateDefault);
    const [periodsToShowValue, setPeriodsToShow] = useState(1);

    useEffect(() => {
        moment.updateLocale(dateLocale, {
            week: {
                dow: firstDayOfWeek, // defaults to monday
            }
        });

    }, [firstDayOfWeek]);

    // The calendar's lower bound is calendarMinDate when supplied, which lets the calendar reach an
    // earlier date than the arrow/anchor floor minDateValue; otherwise it falls back to minDateValue.
    // An early pick is clamped to the nearest valid window by recalcIsoPeriod, so the grid is unchanged.
    const datePickerPopover = (
        <DatePicker
            value={(reverse || periodType === 'isoPeriod') ? null : new Date(periodStart)}
            onChange={onCalendarDaySelect}
            locale={enGB}
            minDate={calendarMinDate ?? minDateValue}
            maxDate={maxDateValue}
            modifiers={calendarModifiers}
            highlightCurrentDay={false}
        />
    );

    function movePeriod(amount) {
        setPeriodOffset(amount);
    }

    function resetPeriod(forceReset) {
        if (forceReset && resetDate) {
            setPeriodOffset(initialOffset);
            setBaseDate(resetDate.toString());
            recalculatePeriod(resetDate, periodsToShowValue);
        } else {
            setPeriodOffset(initialOffset);
            setBaseDate(startDate);
        }
    }

    function onDatesChange() {
        if (periodStart != null && periodEnd != null && onChange != null) {
            onChange(periodStart, periodEnd, periodOffset);
        }
        if (periodType === 'day' && periodStart != null && onChange != null) {
            onChange(periodStart);
        }
    }

    const prevPeriodTypeRef = useRef(periodType);
    
    useEffect(() => {
        const prevType = prevPeriodTypeRef.current;
        const nextType = periodType;
        if (!baseDate || !prevType || prevType === nextType) {
            prevPeriodTypeRef.current = nextType;
            return;
        }
        const b = moment(baseDate);
        const off = periodOffset ?? 0;
        
        let anchor;

        switch (prevType) {
            case 'weeks':
                anchor = moment(b).startOf('isoWeek').add(off, 'weeks');
                break;
            case 'day':
                anchor = moment(b).startOf('day').add(off, 'days');
                break;
            default: // months
                anchor = moment(b).startOf('month').add(off, 'months');
        }

        let newOffset;

        switch (nextType) {
            case 'weeks':
                newOffset = moment(anchor).startOf('isoWeek').diff(moment(b).startOf('isoWeek'), 'weeks');
                break;
            case 'day':
                newOffset = moment(anchor).startOf('day').diff(moment(b).startOf('day'), 'days');
                break;
            default: // months
                newOffset = moment(anchor).startOf('month').diff(moment(b).startOf('month'), 'months');
                break;
        }
        setPeriodOffset(newOffset);
        prevPeriodTypeRef.current = nextType;
    }, [periodType, baseDate, periodOffset]);

    useEffect(function () {
        setBaseDate(startDate);
        setPeriodStart(startDate);
        setPeriodOffset(0);
    }, [startDate]);

    useEffect(function () {
        if (initialOffset == null) {
            setInitialOffset(initialPeriodOffset);
        }
    }, [initialPeriodOffset]);

    useEffect(resetPeriod, [initialOffset]);
    useEffect(recalculatePeriod, [baseDate, periodOffset, periodsToShowValue, periodType]);
    useEffect(onDatesChange, [periodStart, periodEnd]);

    useEffect(function () {
        if (periodsToShow) {
            setPeriodsToShow(periodsToShow);
        }
    }, [periodsToShow]);

     useEffect(function () {
        if (recalcPeriodWithDate?.startDate && recalcPeriodWithDate?.periodsToShow) {
            recalculatePeriod(recalcPeriodWithDate.startDate, recalcPeriodWithDate.periodsToShow);
            setPeriodsToShow(recalcPeriodWithDate.periodsToShow);
        }
    }, [recalcPeriodWithDate]);

    // ISO-period navigation: the window spans `periodsToUse` four-week periods and is anchored at
    // its most-recent (end) period, extending backward. The anchor is tracked on baseDate so that
    // arrow navigation accumulates and Reset (which restores baseDate to resetDate) returns to the
    // start. Each arrow click steps baseDate one whole ISO period (4 weeks) via periodOffset; a
    // calendar pick re-anchors baseDate to the ISO period containing the picked date.
    function recalcIsoPeriod(periodsToUse, selectedDate = null) {
        if (baseDate == null) {
            return;
        }

        let anchorPeriodStart;

        if (selectedDate != null) {
            anchorPeriodStart = getIsoPeriodStart(selectedDate);
        } else {
            // periodOffset counts ISO periods; each period is 4 ISO weeks.
            anchorPeriodStart = getIsoPeriodStart(baseDate).add(periodOffset * isoWeeksPerPeriod, 'weeks');
        }

        // Clamp the anchor so the window cannot extend past the most-recent allowed period: the
        // forward arrow stops at the period containing maxDate (today on the ER dashboard).
        const maxAnchorPeriodStart = getIsoPeriodStart(maxDateValue);
        if (anchorPeriodStart.isAfter(maxAnchorPeriodStart)) {
            anchorPeriodStart = maxAnchorPeriodStart;
        }

        // Clamp the anchor so the window cannot extend before the earliest allowed period: the back
        // arrow stops at the period containing minDate, keeping arrow navigation consistent with the
        // calendar's minDate floor.
        let minAnchorPeriodStart = getIsoPeriodStart(minDateValue);

        // Guard against a floor later than the forward bound (e.g. a young account whose computed
        // minDate crosses maxDate): the forward bound always wins, so a crossed minDate never pushes
        // the anchor past the most-recent allowed period.
        if (minAnchorPeriodStart.isAfter(maxAnchorPeriodStart)) {
            minAnchorPeriodStart = maxAnchorPeriodStart;
        }

        if (anchorPeriodStart.isBefore(minAnchorPeriodStart)) {
            anchorPeriodStart = minAnchorPeriodStart;
        }

        const windowStart = moment(anchorPeriodStart).add(-((periodsToUse - 1) * isoWeeksPerPeriod), 'weeks');
        let windowEnd = moment(anchorPeriodStart).add(isoWeeksPerPeriod, 'weeks').add(-1, 'd');

        // When the most-recent period is still in progress, the range ends at today rather than the
        // period's (future) last day.
        if (windowEnd.isAfter(maxDateValue)) {
            windowEnd = moment(maxDateValue);
        }

        // Fold the navigation back into baseDate so the next click accumulates from here and Reset
        // has a clean anchor to restore.
        setBaseDate(anchorPeriodStart.format(dateFormat));
        setPeriodStart(windowStart.format(dateFormat));
        setPeriodEnd(windowEnd.format(dateFormat));
        setPeriodOffset(0);
    }

    function recalc(period, periodsToUse, selectedDate = null, reset = false) {

        if (periodStart != null) {
            var newPeriodStartDate = moment(periodStart).add(periodOffset, period);
            if (reset) {
                newPeriodStartDate = moment(props.startDate);
            }
            if (selectedDate != null) {
                if (reverse)  {
                    const alignment = period === 'W' ? 'isoWeek' : 'month';
                    newPeriodStartDate = moment(selectedDate).startOf(alignment).add(-(periodsToUse-1), period);
                } else {
                    newPeriodStartDate = moment(selectedDate);
                }
            }

            setPeriodStart(newPeriodStartDate.format(dateFormat));

            var periodEndDate = newPeriodStartDate;
            periodEndDate.add(periodsToUse, period);
            periodEndDate.add(-1, 'd');
            setPeriodEnd(periodEndDate.format(dateFormat));
            setPeriodOffset(0);
        }
    }

    function recalculatePeriod(selectedDate = null, periodsToShowOverride = null, reset = false) {
        
        if (!baseDate) {
            return;
        }

        let periodsToShowToUse = periodsToShowOverride ?? periodsToShowValue;

        var calculationStartPeriod = (periodOffset == null ? 0 : periodOffset);
        
        if (periodStart != null) {
                
            switch (periodType) {
                case "445":
                    recalculateFourFourFivePeriod(calculationStartPeriod);
                    break;
                case "day":
                    var periodStartDate = moment(periodStart);
                    if (reset) {
                        periodStartDate = moment(props.startDate);
                    }
                    if (selectedDate != null) {                            
                        setPeriodStart(moment(selectedDate).format(dateFormat));                        
                    }else {
                        setPeriodStart(periodStartDate.add("d", calculationStartPeriod).format(dateFormat));
                    }
                    setPeriodOffset(0);

                    break;
                case "weeks":
                    recalc('W', periodsToShowToUse, selectedDate, reset);
                    break;
                case "isoPeriod":
                    recalcIsoPeriod(periodsToShowToUse, selectedDate);
                    break;
                default:
                    //months
                    recalc('M', periodsToShowToUse, selectedDate, reset);
            }
            
        }
    }

    function recalculateFourFourFivePeriod(calculationStartPeriod) {
        var startIsoWeek = moment(startDate).startOf('isoWeek');
        var firstIsoWeekOfYear = moment(startIsoWeek).isoWeek(1);

        var fourFourFiveStart = moment(firstIsoWeekOfYear);
        var fourFourFiveNextStart = moment(firstIsoWeekOfYear).add(4, 'W');
        var fourFourFiveEnd = moment(fourFourFiveNextStart).add(-1, 'd')

        var periodWeeks = 9;

        //Work out the current period we are in

        while (fourFourFiveEnd.isBefore(startIsoWeek)) {

            fourFourFiveStart = moment(fourFourFiveNextStart);

            if (periodWeeks > 5) {
                fourFourFiveNextStart.add(4, 'w');
                periodWeeks -= 4;
            } else {

                if (fourFourFiveStart.isoWeek() === 48 && fourFourFiveStart.isoWeeksInYear() === 53) {
                    fourFourFiveNextStart.add(6, 'w');
                } else {
                    fourFourFiveNextStart.add(5, 'w');
                }

                periodWeeks = 13;
            }

            fourFourFiveEnd = moment(fourFourFiveNextStart).add(-1, 'd');
        }

        //If going backwards, start subtracting 445 periods

        if (calculationStartPeriod < 0) {

            if (periodWeeks === 13) {
                periodWeeks = 5;
            } else {
                periodWeeks += 4;
            }

            for (var i = 0; i > calculationStartPeriod; i--) {

                fourFourFiveNextStart = moment(fourFourFiveStart);

                if (periodWeeks >= 13) {
                    fourFourFiveStart.add(-5, 'w');
                    if (fourFourFiveStart.isoWeek() === 49 && fourFourFiveStart.isoWeeksInYear() === 53) {
                        fourFourFiveStart.add(-1, 'w');
                    }

                    periodWeeks = 5;
                } else {

                    fourFourFiveStart.add(-4, 'w');
                    periodWeeks += 4;
                }

                fourFourFiveEnd = moment(fourFourFiveNextStart).add(-1, 'd');
            }

        } else if (calculationStartPeriod > 0) {

            //If moving forwards, start adding 445 periods

            for (var i = 0; i < calculationStartPeriod; i++) {

                fourFourFiveStart = moment(fourFourFiveNextStart);

                if (periodWeeks > 5) {
                    fourFourFiveNextStart.add(4, 'w');
                    periodWeeks -= 4;
                } else {

                    if (fourFourFiveStart.isoWeek() === 48 && fourFourFiveStart.isoWeeksInYear() === 53) {
                        fourFourFiveNextStart.add(6, 'w');
                    } else {
                        fourFourFiveNextStart.add(5, 'w');
                    }

                    periodWeeks = 13;
                }

                fourFourFiveEnd = moment(fourFourFiveNextStart).add(-1, 'd');
            }

        }

        setPeriodStart(fourFourFiveStart.format(dateFormat));
        setPeriodEnd(fourFourFiveEnd.format(dateFormat));
    }

    function setMinDateFromProps() {
        const newMinDate = minDate || minDateDefault;
        setMinDate(newMinDate);
    }
    function setMaxDateFromProps() {
        const newMaxDate = maxDate || maxDateDefault;
        setMaxDate(newMaxDate);
    }

    function onCalendarDaySelect(selectedDate, isUserChange) {

        if (!isUserChange) {
            return;
        }

        recalculatePeriod(selectedDate);
        setIsCalendarOpen(false);
    }

    useEffect(setMinDateFromProps, [minDate]);
    useEffect(setMaxDateFromProps, [maxDate]);

    // In isoPeriod mode the anchor (baseDate, folded by recalcIsoPeriod) cannot move past the forward
    // bound (maxDate) or before the backward bound (minDate). When it has reached either, disable the
    // corresponding arrow instead of leaving a silent no-op at the boundary.
    const atForwardBound = periodType === 'isoPeriod' && baseDate != null
        && getIsoPeriodStart(baseDate).isSameOrAfter(getIsoPeriodStart(maxDateValue), 'day');
    const atBackwardBound = periodType === 'isoPeriod' && baseDate != null
        && getIsoPeriodStart(baseDate).isSameOrBefore(getIsoPeriodStart(minDateValue), 'day');

    return (
        <div className="form-field">
            <Button text={showText ? "Previous" : ""} intent="secondary" large={false} minimal={true} icon="fast-backward" onClick={() => movePeriod(-1)} disabled={disabled || atBackwardBound || (minPeriods != null && periodOffset <= minPeriods)} loading={loading}></Button>
            <ShowHide
                evaluator={showCalendar}
                show={(
                    <Popover content={datePickerPopover} placement="bottom" usePortal={usePortal}
                        isOpen={isCalendarOpen}
                        onInteraction={setIsCalendarOpen}>
                        <Button disabled={disabled} icon="calendar" intent="secondary" iconOnly minimal large={false} onClick={() => { }} loading={loading}/>
                    </Popover>
                )}
            />
            <ShowHide
                evaluator={periodType === 'day'}
                show={<span className={classNames("form-period-display", { "bp6-skeleton": loading })}>{periodStart}</span>}
                hide={<span className={classNames("form-period-display", { "bp6-skeleton": loading })}>{periodStart} - {periodEnd}</span>}
            />
            <Button text={showText ? "Next" : ""} intent="secondary" large={false} minimal={true} icon="fast-forward" onClick={() => movePeriod(1)} disabled={disabled || atForwardBound || (maxPeriods != null && periodOffset >= maxPeriods)} loading={loading}></Button>
            <ShowHide evaluator={showResetButton} 
                show={<Button text={showText ? "Reset" : ""} intent="secondary" large={false} minimal={true} icon="reset" onClick={() => resetPeriod(true)} disabled={disabled} loading={loading}></Button>}
            />
        </div>
    );
}

FormDatePeriodSelector.propTypes = {
    id: PropTypes.string,
    startDate: PropTypes.string,
    minPeriods: PropTypes.number,
    maxPeriods: PropTypes.number,
    periodType: PropTypes.string,
    periodsToShow: PropTypes.number,
    initialPeriodOffset: PropTypes.number,
    onChange: PropTypes.func.isRequired,
    dateFormat: PropTypes.string,
    showCalendar: PropTypes.bool,
    calendarMinDate: PropTypes.instanceOf(Date),
    calendarMaxDate: PropTypes.instanceOf(Date),
    calendarModifiers: PropTypes.object,
    showText: PropTypes.bool,
    usePortal: PropTypes.bool,
    showResetButton: PropTypes.bool,
    resetDate: PropTypes.string,
    loading: PropTypes.bool,
    recalcPeriodWithDate: PropTypes.object,
    reverse: PropTypes.bool,
    maxDate: PropTypes.instanceOf(Date),
    minDate: PropTypes.instanceOf(Date),
    firstDayOfWeek: PropTypes.number //This must be set consitently on a page. You cannot have on dateInput starting on different days of the week on the same page
};